import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

// Define the schema for budget item data
const budgetItemSchema = z.object({
  tournament_id: z.string().uuid(),
  category: z.string().min(1, { message: "Category is required" }),
  subcategory: z.string().optional(),
  description: z.string().min(1, { message: "Description is required" }),
  allocated_amount: z.number().nonnegative({ message: "Allocated amount cannot be negative" }),
  spent_amount: z.number().nonnegative({ message: "Spent amount cannot be negative" }).optional(),
  fiscal_year: z.number().int().positive().optional(),
  status: z.enum(['active', 'closed', 'archived'])
});

type BudgetItemData = z.infer<typeof budgetItemSchema>;

interface BudgetItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  budgetItem: {
    id: string;
    category: string;
    subcategory: string | null;
    description: string;
    allocated_amount: number;
    spent_amount: number;
    fiscal_year: number | null;
    status: string;
  } | null;
  tournamentId: string;
  onSuccess: () => void;
}

export default function BudgetItemModal({
  isOpen,
  onClose,
  budgetItem,
  tournamentId,
  onSuccess
}: BudgetItemModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const currentYear = new Date().getFullYear();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm<BudgetItemData>({
    resolver: zodResolver(budgetItemSchema),
    defaultValues: {
      tournament_id: tournamentId,
      category: '',
      subcategory: '',
      description: '',
      allocated_amount: 0,
      spent_amount: 0,
      fiscal_year: currentYear,
      status: 'active'
    }
  });

  useEffect(() => {
    if (budgetItem) {
      // Set form values from budget item
      setValue('tournament_id', tournamentId);
      setValue('category', budgetItem.category);
      setValue('subcategory', budgetItem.subcategory || '');
      setValue('description', budgetItem.description);
      setValue('allocated_amount', budgetItem.allocated_amount);
      setValue('spent_amount', budgetItem.spent_amount);
      setValue('fiscal_year', budgetItem.fiscal_year || currentYear);
      setValue('status', budgetItem.status as 'active' | 'closed' | 'archived');
    } else {
      // Reset form for new budget item
      reset({
        tournament_id: tournamentId,
        category: '',
        subcategory: '',
        description: '',
        allocated_amount: 0,
        spent_amount: 0,
        fiscal_year: currentYear,
        status: 'active'
      });
    }
  }, [budgetItem, tournamentId, currentYear, reset, setValue]);

  const onSubmit = async (data: BudgetItemData) => {
    try {
      setIsSubmitting(true);
      setError('');

      // Validate numeric fields before submission
      if (data.allocated_amount < 0) {
        throw new Error('Allocated amount cannot be negative');
      }

      if (budgetItem) {
        // Update existing budget item
        const { error: updateError } = await supabase
          .from('budget_items')
          .update(data)
          .eq('id', budgetItem.id);
        
        if (updateError) throw updateError;
      } else {
        // Create new budget item
        const { error: createError } = await supabase
          .from('budget_items')
          .insert([data]);
        
        if (createError) throw createError;
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Budget item submission error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <div className="absolute right-0 top-0 pr-4 pt-4 z-10">
                  <button
                    type="button"
                    className="rounded-full bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-golf-orange focus:ring-offset-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="bg-golf-sand/30 px-4 py-5 sm:px-6">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      {budgetItem ? 'Edit' : 'Add'} Budget Item
                    </Dialog.Title>
                  </div>

                  <div className="bg-white px-4 py-5 sm:p-6">
                    {error && (
                      <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
                        <div className="flex">
                          <ExclamationCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                          <span>{error}</span>
                        </div>
                      </div>
                    )}

                    <input
                      type="hidden"
                      {...register('tournament_id')}
                      value={tournamentId}
                    />

                    <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-3">
                        <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                          Category <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-1">
                          <select
                            {...register('category')}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                          >
                            <option value="">Select a category</option>
                            <option value="venue">Venue & Facilities</option>
                            <option value="prize">Prize Money</option>
                            <option value="marketing">Marketing & PR</option>
                            <option value="operations">Staff & Operations</option>
                            <option value="equipment">Equipment</option>
                            <option value="travel">Travel & Accommodation</option>
                            <option value="catering">Catering & Hospitality</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                        {errors.category && (
                          <p className="mt-2 text-sm text-red-600">{errors.category.message}</p>
                        )}
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="subcategory" className="block text-sm font-medium text-gray-700">
                          Subcategory
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            {...register('subcategory')}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                          />
                        </div>
                        {errors.subcategory && (
                          <p className="mt-2 text-sm text-red-600">{errors.subcategory.message}</p>
                        )}
                      </div>

                      <div className="sm:col-span-6">
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                          Description <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            {...register('description')}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                          />
                        </div>
                        {errors.description && (
                          <p className="mt-2 text-sm text-red-600">{errors.description.message}</p>
                        )}
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="allocated_amount" className="block text-sm font-medium text-gray-700">
                          Allocated Amount (₦) <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-1">
                          <input
                            type="number"
                            step="0.01"
                            {...register('allocated_amount', { valueAsNumber: true })}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                          />
                        </div>
                        {errors.allocated_amount && (
                          <p className="mt-2 text-sm text-red-600">{errors.allocated_amount.message}</p>
                        )}
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="fiscal_year" className="block text-sm font-medium text-gray-700">
                          Fiscal Year
                        </label>
                        <div className="mt-1">
                          <input
                            type="number"
                            {...register('fiscal_year', { valueAsNumber: true })}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                          />
                        </div>
                        {errors.fiscal_year && (
                          <p className="mt-2 text-sm text-red-600">{errors.fiscal_year.message}</p>
                        )}
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                          Status <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-1">
                          <select
                            {...register('status')}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                          >
                            <option value="active">Active</option>
                            <option value="closed">Closed</option>
                            <option value="archived">Archived</option>
                          </select>
                        </div>
                        {errors.status && (
                          <p className="mt-2 text-sm text-red-600">{errors.status.message}</p>
                        )}
                      </div>

                      {budgetItem && (
                        <div className="sm:col-span-3">
                          <label htmlFor="spent_amount" className="block text-sm font-medium text-gray-700">
                            Spent Amount (₦)
                          </label>
                          <div className="mt-1">
                            <input
                              type="number"
                              step="0.01"
                              {...register('spent_amount', { valueAsNumber: true })}
                              disabled
                              className="block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              This value is automatically calculated from transactions
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="inline-flex w-full justify-center rounded-md border border-transparent bg-golf-orange px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-golf-orange/90 focus:outline-none focus:ring-2 focus:ring-golf-orange focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      {isSubmitting ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-golf-orange focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}